import Home from "./pages/Home";
import React from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import { Provider } from "react-redux";
import { persistor, store } from "./store/index";
import { PersistGate } from "redux-persist/integration/react";
import FullscreenProgress from "./components/layout/FullscreenProgress";

import MainLayout from "./layouts/MainLayout";
import Login from "./pages/Login";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProtectedRoute from "./components/common/ProtectedRoute";
import Restaurants from "./pages/Restaurants";
import Cities from "./pages/Cities";
import Riders from "./pages/Riders";
import Orders from "./pages/Orders";
import Items from "./pages/Items";
import ModifierGroups from "./pages/ModifierGroups";
import ModifierOptions from "./pages/ModifierOptions";
import Admins from "./pages/Admins";
import CreateAdmin from "./pages/CreateAdmin";
import CreateRestaurant from "./pages/CreateRestaurant";
import EditRestaurant from "./pages/EditRestaurant";
import CreateModifierOption from "./pages/CreateModifierOption";
import EditModifierOption from "./pages/EditModifierOption";
import CreateModifierGroup from "./pages/CreateModifierGroup";
import EditModifierGroup from "./pages/EditModifierGroup";
import CreateItem from "./pages/CreateItem";
import EditItem from "./pages/EditItem";
import Headers from "./pages/Headers";
import CreateHeader from "./pages/CreateHeader";
import EditHeader from "./pages/EditHeader";
import CreateCity from "./pages/CreateCity";
import EditCity from "./pages/EditCity";
import CreateRider from "./pages/CreateRider";
import EditRider from "./pages/EditRider";
import RestaurantApplications from "./pages/RestaurantApplications";
import RiderApplications from "./pages/RiderApplications";
import OrderDetails from "./pages/OrderDetails";
import EditAdmin from "./pages/EditAdmin";
import Ads from "./pages/Ads";
import CreateAd from "./pages/CreateAd";
import EditAd from "./pages/EditAd";

const App: React.FC = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Main Layout Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute
              Element={MainLayout}
              redirectTo="/login"
            />
          }
        >
          <Route
            index
            element={<Home />}
          />
          <Route path="restaurants">
            <Route
              index
              element={<Restaurants />}
            />
            <Route
              path="create"
              element={<CreateRestaurant />}
            />
            <Route
              path=":restaurantId"
              element={<EditRestaurant />}
            />
          </Route>
          <Route path="cities">
            <Route
              index
              element={<Cities />}
            />
            <Route
              path="create"
              element={<CreateCity />}
            />
            <Route
              path=":cityId"
              element={<EditCity />}
            />
          </Route>
          <Route path="orders">
            <Route
              index
              element={<Orders />}
            />
            <Route
              path=":orderId"
              element={<OrderDetails />}
            />
          </Route>
          <Route path="items">
            <Route
              index
              element={<Items />}
            />
            <Route
              path="create"
              element={<CreateItem />}
            />
            <Route
              path=":itemId"
              element={<EditItem />}
            />
          </Route>
          <Route path="modifier-groups">
            <Route
              index
              element={<ModifierGroups />}
            />
            <Route
              path="create"
              element={<CreateModifierGroup />}
            />
            <Route
              path=":modifierGroupId"
              element={<EditModifierGroup />}
            />
          </Route>
          <Route path="modifier-options">
            <Route
              index
              element={<ModifierOptions />}
            />
            <Route
              path="create"
              element={<CreateModifierOption />}
            />
            <Route
              path=":modifierOptionId"
              element={<EditModifierOption />}
            />
          </Route>
          <Route path="headers">
            <Route
              index
              element={<Headers />}
            />
            <Route
              path="create"
              element={<CreateHeader />}
            />
            <Route
              path=":headerId"
              element={<EditHeader />}
            />
          </Route>
          <Route path="riders">
            <Route
              index
              element={<Riders />}
            />
            <Route
              path="create"
              element={<CreateRider />}
            />
            <Route
              path=":riderId"
              element={<EditRider />}
            />
          </Route>
          <Route path="admins">
            <Route
              index
              element={<Admins />}
            />
            <Route
              path="create"
              element={<CreateAdmin />}
            />
            <Route
              path=":adminId"
              element={<EditAdmin />}
            />
          </Route>
          <Route path="ads">
            <Route
              index
              element={<Ads />}
            />
            <Route
              path="create"
              element={<CreateAd />}
            />
            <Route
              path=":adId"
              element={<EditAd />}
            />
          </Route>
          <Route
            element={<RestaurantApplications />}
            path="/applications/restaurants"
          />
          <Route
            element={<RiderApplications />}
            path="/applications/riders"
          />
        </Route>
        <Route
          element={<Login />}
          path="/login"
        />
      </>
    )
  );

  return (
    <Provider store={store}>
      <PersistGate
        loading={<FullscreenProgress />}
        persistor={persistor}
      >
        <RouterProvider router={router} />
        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </PersistGate>
    </Provider>
  );
};

export default App;
